import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTheme } from "../../contexts/ThemeContext";

export default function LightSwitch() {
  const { theme, toggle } = useTheme();
  const isOn = theme === "light"; // light = ON, dark = OFF
  const reduced = useReducedMotion();
  const [pulseKey, setPulseKey] = useState(0);

  // Bump pulse on every click
  useEffect(() => {
    setPulseKey((k) => k + 1);
  }, [theme]);

  const onClick = () => {
    toggle();
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  };

  const spring = reduced
    ? { type: "tween", duration: 0.2 }
    : { type: "spring", stiffness: 400, damping: 17 };

  return (
    <button
      type="button"
      aria-label="Toggle dark mode"
      aria-pressed={!isOn}
      data-testid="theme-light-switch"
      onClick={onClick}
      onKeyDown={onKey}
      className="group fixed left-5 bottom-5 sm:left-6 sm:bottom-6 z-[60] flex flex-col items-center select-none outline-none focus-visible:ring-2 focus-visible:ring-[#B8D958] rounded-2xl"
      style={{ cursor: "pointer" }}
    >
      {/* ON label */}
      <span
        className={`text-[10px] font-bold uppercase tracking-[0.25em] mb-1.5 transition-colors ${
          isOn ? "text-[#6FA235]" : "text-[#888]"
        }`}
      >
        ON
      </span>

      {/* Plate */}
      <motion.div
        whileHover={reduced ? {} : { scale: 1.05 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className={`relative rounded-[12px] transition-colors duration-300 ls-plate ${
          isOn
            ? "bg-[#F5F0E6] shadow-[inset_0_1px_2px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.12),0_8px_20px_rgba(0,0,0,0.08)]"
            : "bg-[#1A1A1A] shadow-[inset_0_1px_2px_rgba(255,255,255,0.04),0_0_0_1px_rgba(111,162,53,0.15),0_0_28px_rgba(111,162,53,0.10)]"
        }`}
      >
        {/* Screws */}
        <span
          className={`absolute top-2 right-2 h-1.5 w-1.5 rounded-full ${
            isOn ? "bg-[#3a3a3a]/50" : "bg-[#6FA235]/40"
          }`}
        />
        <span
          className={`absolute bottom-2 left-2 h-1.5 w-1.5 rounded-full ${
            isOn ? "bg-[#3a3a3a]/50" : "bg-[#6FA235]/40"
          }`}
        />

        {/* LED */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2">
          <motion.span
            key={pulseKey}
            initial={{ scale: 1 }}
            animate={
              reduced
                ? { scale: 1 }
                : { scale: [1, 1.45, 1] }
            }
            transition={{ duration: 0.22 }}
            className="block h-1.5 w-1.5 rounded-full"
            style={{
              backgroundColor: isOn ? "#6FA235" : "#444",
              boxShadow: isOn
                ? "0 0 4px rgba(111,162,53,0.5), 0 0 10px rgba(111,162,53,0.35)"
                : "none",
            }}
          />
          {/* Idle hover pulse */}
          {isOn && !reduced && (
            <motion.span
              animate={{ scale: [1, 1.6, 1], opacity: [0.4, 0, 0.4] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
              className="absolute inset-0 h-1.5 w-1.5 rounded-full bg-[#6FA235]"
              style={{ filter: "blur(2px)" }}
            />
          )}
        </div>

        {/* Rocker track */}
        <div
          className={`absolute left-1/2 -translate-x-1/2 rounded-[8px] ${
            isOn ? "bg-[#E6DFD0]" : "bg-[#0E0E0E]"
          }`}
          style={{ top: 22, width: 36, height: 64 }}
        >
          {/* Rocker */}
          <motion.div
            layout
            transition={spring}
            className={`absolute left-0 right-0 rounded-[6px] ${
              isOn
                ? "bg-gradient-to-b from-[#FBFAF5] to-[#E8E1D0] shadow-[inset_0_1px_0_rgba(255,255,255,0.9),inset_0_-1px_0_rgba(0,0,0,0.08),0_2px_4px_rgba(0,0,0,0.15)]"
                : "bg-gradient-to-b from-[#2A2A2A] to-[#0E0E0E] shadow-[inset_0_1px_0_rgba(255,255,255,0.05),inset_0_-1px_0_rgba(0,0,0,0.6),0_2px_4px_rgba(0,0,0,0.4)]"
            }`}
            style={{
              height: 36,
              top: isOn ? 0 : 28,
            }}
          >
            {/* Bevel highlight line */}
            <span
              className={`absolute left-2 right-2 h-px ${
                isOn ? "top-1/2 bg-black/10" : "top-1/2 bg-white/5"
              }`}
            />
          </motion.div>
        </div>
      </motion.div>

      {/* OFF label */}
      <span
        className={`text-[10px] font-bold uppercase tracking-[0.25em] mt-1.5 transition-colors ${
          !isOn ? "text-[#6FA235]" : "text-[#888]"
        }`}
      >
        OFF
      </span>
    </button>
  );
}
